import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map, take } from "rxjs/operators";
import { BaseHttpService } from "../../core/services/base-http.service";
import { StorageService } from "../../core/storage/storage.service";
import { config } from "../../shared/config";
import { UserAccount } from "../models";
import { PrivilegeSchema } from "src/app/settings/organisms/users-permission-settings/models/privilege-schema.type";
import { PrivilegeGroupSchema } from "src/app/settings/organisms/users-permission-settings/models/privilege-group-schema.type";
import { PrivilegeUserGroup } from "src/app/settings/organisms/users-permission-settings/models/privilege-user-group.type";
import { UnitGroups } from "src/app/units/models/unit-groups";
import { NewUserAccount } from "src/app/settings/organisms/users-permission-settings/models/new-user-account.type";

@Injectable({
  providedIn: "root",
})
export class ClientManagementService extends BaseHttpService {
  constructor(
    protected _http: HttpClient,
    protected _storage: StorageService,
  ) {
    super(_http, _storage);
  }

  getClientUsers(): Observable<UserAccount[]> {
    const apiUrl = `${config.generateApiUrl(
      "management/clients/users",
      null,
      null,
      "2.2"
    )}`;

    return this.get<UserAccount[]>(apiUrl).pipe(
      map((res: UserAccount[]) => res["data"]["users"])
    );
  }

  addNewUserAccountForClient(newUserAccount: NewUserAccount): Observable<any> {
    const payload = newUserAccount;

    const apiUrl = `${config.generateApiUrl(
      "management/users",
      null,
      null,
      "1.8"
    )}`;

    return this._http
      .post<any>(apiUrl, payload)
      .pipe(map((res: any) => res["data"]));
  }

  getGroupsByClientId(clientId: number): Observable<PrivilegeUserGroup[]> {
    const apiUrl = `${config.generateApiUrl(
      `management/PrivilegeUserGroup/client/${clientId}`,
      null,
      null,
      "2.2"
    )}`;

    return this.get<PrivilegeUserGroup[]>(apiUrl).pipe(
      map((res: PrivilegeUserGroup[]) => res["data"])
    );
  }

  getGroupsByUserAccountId(
    userAccountId: number
  ): Observable<PrivilegeUserGroup[]> {
    const apiUrl = `${config.generateApiUrl(
      `management/PrivilegeUserGroup/userAccount/${userAccountId}`,
      null,
      null,
      "2.2"
    )}`;

    return this.get<PrivilegeUserGroup[]>(apiUrl).pipe(
      map((res: PrivilegeUserGroup[]) => res["data"])
    );
  }

  deleteUserGroupFromClient(userGroupId: number): Observable<any> {
    const apiUrl = `${config.generateApiUrl(
      `management/PrivilegeUserGroup/${userGroupId}`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .delete<any[]>(apiUrl)
      .pipe(map((res: any[]) => res["data"]));
  }

  addNewUserGroupForClient(
    userGroupName: string,
    clientId: number
  ): Observable<any> {
    const payload = {
      name: userGroupName,
      clientId,
    };
    const apiUrl = `${config.generateApiUrl(
      `management/PrivilegeUserGroup/client`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .post<any[]>(apiUrl, payload)
      .pipe(map((res: any[]) => res["data"]));
  }

  getAllUserPrivileges(userAccountId: number): Observable<PrivilegeSchema[]> {
    const apiUrl = `${config.generateApiUrl(
      `privilege/user/${userAccountId}`,
      null,
      null,
      "2.2"
    )}`;

    return this.get<PrivilegeSchema[]>(apiUrl).pipe(
      map((res: PrivilegeSchema[]) => res["data"])
    );
  }

  getAllPrivilegesSchemas(): Observable<PrivilegeSchema[]> {
    const apiUrl = `${config.generateApiUrl(
      `privilege/schemas`,
      null,
      null,
      "2.2"
    )}`;

    return this.get<PrivilegeSchema[]>(apiUrl).pipe(
      map((res: any) => {
        return res.data.sort((a: PrivilegeSchema, b: PrivilegeSchema) => {
          if (a.key < b.key) {
            return -1;
          } else if (a.key > b.key) {
            return 1;
          } else {
            return 0;
          }
        });
      })
    );
  }

  getAllGroupPrivileges(
    privilegeGroupId: number
  ): Observable<PrivilegeGroupSchema[]> {
    const apiUrl = `${config.generateApiUrl(
      `privilege/allPrivileges/group/${privilegeGroupId}`,
      null,
      null,
      "2.2"
    )}`;

    return this.get<PrivilegeGroupSchema[]>(apiUrl).pipe(
      take(1),
      map((res: PrivilegeGroupSchema[]) => res["data"])
    );
  }

  getGroupById(privilegeGroupId: number): Observable<PrivilegeUserGroup> {
    const apiUrl = `${config.generateApiUrl(
      `management/privilegeUserGroup/${privilegeGroupId}`,
      null,
      null,
      "2.2"
    )}`;

    return this.get<PrivilegeUserGroup>(apiUrl).pipe(
      map((res: PrivilegeUserGroup) => res["data"])
    );
  }

  changeSetting(userAccountId: number, key: string, value: string) {
    const apiUrl = `${config.generateApiUrl(
      `settings/userAccountId/${userAccountId}/${key}/${value}`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .put<any[]>(apiUrl, {})
      .pipe(map((res: any[]) => res["data"]));
  }

  addPrivilegeToGroup(privilegeGroupId: number, privilegeSchemaId: number) {
    const apiUrl = `${config.generateApiUrl(
      `privilege/group/${privilegeGroupId}/${privilegeSchemaId}`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .post<any[]>(apiUrl, null)
      .pipe(map((res: any[]) => res["data"]));
  }

  deletePrivilegeFromGroup(privilegeGroupId: number, schemaId: number) {
    const apiUrl = `${config.generateApiUrl(
      `privilege/group/${privilegeGroupId}/${schemaId}`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .delete<any[]>(apiUrl)
      .pipe(map((res: any[]) => res["data"]));
  }

  changeUserEmail(
    userAccountId: number,
    newEmail: string
  ): Observable<UserAccount> {
    const fields = ["*"];
    const apiUrl = `${config.generateApiUrl(
      `userAccount/${userAccountId}`,
      fields,
      null,
      "2.2"
    )}`;

    return this._http
      .put<UserAccount>(apiUrl, { newEmail })
      .pipe(map((res: UserAccount) => res["data"]));
  }

  changeUserState(
    userAccountId: number,
    state: 10 | 20
  ): Observable<UserAccount> {
    const fields = ["*"];
    const apiUrl = `${config.generateApiUrl(
      `userAccount/changeState/${userAccountId}/${state}`,
      fields,
      null,
      "2.2"
    )}`;

    return this._http
      .put<UserAccount>(apiUrl, {})
      .pipe(map((res: UserAccount) => res["data"]));
  }

  addGroupForUsers(userAccountIds: number[], name: string) {
    const payload = { userAccountIds, name };
    const apiUrl = `${config.generateApiUrl(
      `management/PrivilegeUserGroup`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .post<any[]>(apiUrl, payload)
      .pipe(map((res: any[]) => res["data"]));
  }

  assignGroupForUser(userAccountId: number, privilegeGroupId: number) {
    const apiUrl = `${config.generateApiUrl(
      `management/PrivilegeUserGroup/assign/userAccount/${userAccountId}/privilegeUserGroup/${privilegeGroupId}`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .put<any[]>(apiUrl, {})
      .pipe(map((res: any[]) => res["data"]));
  }

  unassignUserFromGroup(
    userAccountId: number,
    privilegeGroupId: number
  ): Observable<any> {
    const apiUrl = `${config.generateApiUrl(
      `management/PrivilegeUserGroup/unassign/userAccount/${userAccountId}/privilegeUserGroup/${privilegeGroupId}`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .put<any[]>(apiUrl, {})
      .pipe(map((res: any[]) => res["data"]));
  }

  changeGroupName(privilegeGroupId: number, name: string) {
    const apiUrl = `${config.generateApiUrl(
      `management/PrivilegeUserGroup/name/${privilegeGroupId}/${name}`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .put<any[]>(apiUrl, {})
      .pipe(map((res: any[]) => res["data"]));
  }

  deleteUser(userAccountId: number) {
    const apiUrl = `${config.generateApiUrl(
      `management/users/${userAccountId}`,
      null,
      null,
      "1.8"
    )}`;

    return this._http
      .delete<any[]>(apiUrl)
      .pipe(map((res: any[]) => res["data"]));
  }

  addPrivilegeForUser(
    userAccountId: number,
    privilegeSchemaId: number
  ): Observable<PrivilegeSchema> {
    const apiUrl = `${config.generateApiUrl(
      `Privilege/user/${userAccountId}/${privilegeSchemaId}`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .post<PrivilegeSchema[]>(apiUrl, null)
      .pipe(map((res: PrivilegeSchema[]) => res["data"]));
  }

  deletePrivilegeForUser(
    userAccountId: number,
    privilegeSchemaId: number
  ): Observable<PrivilegeSchema> {
    const apiUrl = `${config.generateApiUrl(
      `Privilege/user/${userAccountId}/${privilegeSchemaId}`,
      null,
      null,
      "2.2"
    )}`;

    return this._http.delete<PrivilegeSchema>(apiUrl);
  }

  getUnitGroupsForClient(): Observable<UnitGroups[]> {
    const apiUrl = `${config.generateApiUrl(
      `management/unitGroups`,
      ["*"],
      null,
      "1.8"
    )}`;

    return this._http
      .get<UnitGroups[]>(apiUrl)
      .pipe(map((res: UnitGroups[]) => res["data"]["items"]));
  }

  assignUnitGroupToUser(
    unitGroupId: number,
    userAccountId: number
  ): Observable<UnitGroups> {
    const payload = {
      unitGroupId,
      userAccountId,
    };
    const apiUrl = `${config.generateApiUrl(
      `unitGroups/addUserToGroup`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .post<UnitGroups[]>(apiUrl, payload)
      .pipe(map((res: UnitGroups[]) => res["data"]));
  }

  unasignUnitGroupToUser(
    unitGroupId: number,
    userAccountId: number
  ): Observable<UnitGroups> {
    const payload = {
      unitGroupId,
      userAccountId,
    };
    const apiUrl = `${config.generateApiUrl(
      `unitGroups/removeUserFromGroup`,
      null,
      null,
      "2.2"
    )}`;

    return this._http
      .put<UnitGroups[]>(apiUrl, payload)
      .pipe(map((res: UnitGroups[]) => res["data"]));
  }
}
